/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/components/FormField.tsx
 * Release: 2.0.0
 * Descrizione: Componente per gestire i singoli campi del form con supporto custom registry e defaultValue
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import type { FormFieldType } from '../types';
import { CountrySelectField } from '../custom/CountrySelectField';
import { PhonePrefixSelectField } from '../custom/PhonePrefixSelectField';
import SocialCheckboxRow from '../custom/SocialCheckboxRow';
import AddressSection from '../custom/AddressSection';
import { MapPlaceholder } from '../custom/MapPlaceholder';
import GoogleMapsAutocomplete from '../custom/GoogleMapsAutocomplete';
import { InteractiveMap } from '../custom/InteractiveMap';
import RichTextEditor from '../custom/RichTextEditor';
import { PrivacyLinks } from '../custom/PrivacyLinks';
import { GooglePlacesAutocomplete } from '../custom/GooglePlacesAutocomplete';
import PaymentMethodsCustom from '../custom/PaymentMethodsCustom';
import DeliveryMethodsCustom from '../custom/DeliveryMethodsCustom';
import { ClienteSearchField } from '../fields/ClienteSearchField';
import { ClienteDetailsDisplay } from '../fields/ClienteDetailsDisplay';
// import { CheckinSelectField } from '../fields/CheckinSelectField'; // Temporarily disabled
import { MezzoSelectField } from '../fields/MezzoSelectField';
import { MultiSelectGuasti } from '../fields/MultiSelectGuasti';
import FaultDescriptionCustom from '../custom/FaultDescriptionCustom';
import { MarcaTurbinaField } from '../fields/MarcaTurbinaField';
import { ToggleButton } from '../custom/ToggleButton';
import { SubsectionHeader } from '../custom/SubsectionHeader';
import { formatters } from '../../forms/utils/formatters';

const customFieldRegistry: Record<string, React.FC<any>> = {
  CountrySelectField,
  PhonePrefixSelectField,
  SocialCheckboxRow,
  AddressSection,
  MapPlaceholder,
  GoogleMapsAutocomplete,
  InteractiveMap,
  RichTextEditor,
  PrivacyLinks,
  GooglePlacesAutocomplete,
  PaymentMethodsCustom,
  DeliveryMethodsCustom,
  ClienteSearchField,
  ClienteDetailsDisplay,
  // CheckinSelectField, // Temporarily disabled
  MezzoSelectField,
  MultiSelectGuasti,
  FaultDescriptionCustom,
  MarcaTurbinaField,
  ToggleButton,
  SubsectionHeader,
};

interface FormFieldProps {
  field: FormFieldType;
  value: any;
  error?: string;
  onChange: (value: any) => void;
  onFocus?: () => void;
  disabled?: boolean;
  showError?: boolean;
  tabIndex?: number;
  readOnly?: boolean;
  formValues?: Record<string, any>;
}

// Contatore globale per debug
let fieldRenderCount = 0;

export const FormField: React.FC<FormFieldProps> = ({
  field,
  value,
  error,
  onChange,
  onFocus,
  disabled = false,
  showError = true,
  tabIndex,
  readOnly = false,
  formValues
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showReadOnlyHint, setShowReadOnlyHint] = useState(false);

  // Tooltip per campi in readOnly mode (fallback)
  const readOnlyTooltip = readOnly ? 'Clicca "Modifica" per abilitare le modifiche' : undefined;

  // Mostra hint al click su campo readOnly (preserva selezione testo)
  const handleReadOnlyClick = () => {
    console.log(`🔒 [FormField] Click su campo "${field.label}", readOnly:`, readOnly, 'showReadOnlyHint:', showReadOnlyHint);
    if (readOnly && !showReadOnlyHint) {
      console.log(`🔒 [FormField] Mostro hint per "${field.label}"`);
      setShowReadOnlyHint(true);
      setTimeout(() => setShowReadOnlyHint(false), 2000);
    }
  };

  // Debug: log del valore ricevuto per campi chiave
  if (field.label === 'Email' || field.label === 'Ragione Sociale') {
    fieldRenderCount++;
    console.log(`📧 [FormField #${fieldRenderCount}] ${field.label} riceve value:`, value, 'type:', typeof value);
  }

  // Debug: log per PaymentMethods e DeliveryMethods
  if (field.customComponent === 'PaymentMethodsCustom' || field.customComponent === 'DeliveryMethodsCustom') {
    console.log(`🔍 [FormField] ${field.customComponent} riceve value:`, JSON.stringify(value, null, 2));
  }

  const applyFormatter = (inputValue: string) => {
    if (field.formatter && typeof field.formatter === 'string' && formatters[field.formatter as keyof typeof formatters]) {
      return formatters[field.formatter as keyof typeof formatters](inputValue);
    }
    return inputValue;
  };

  const isValidValue = (val: any): boolean => {
    if (!val) return false;
    if (typeof val === 'string') return val.trim().length > 0;
    if (typeof val === 'boolean') return val;
    if (Array.isArray(val)) return val.length > 0;
    return !!val;
  };

  const getBorderColor = () => {
    if (error) return 'border-red-500';
    if (isFocused) return 'border-blue-500';
    if (isValidValue(value)) return 'border-green-500';
    return 'border-zinc-600';
  };

  // Styling diverso per readOnly vs disabled normale
  // readOnly: testo ben visibile (text-zinc-200), nessuna opacità ridotta
  // disabled: testo sbiadito (text-zinc-400) con opacity-50
  const getInputClasses = () => {
    if (readOnly) {
      return 'bg-zinc-800 cursor-default text-zinc-200'; // Leggibile in sola lettura
    }
    if (disabled) {
      return 'bg-zinc-700 cursor-not-allowed text-zinc-400'; // Sbiadito per disabled
    }
    return 'bg-zinc-700 text-white'; // Normale
  };

  const baseClasses = `
    w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500
    ${getBorderColor()}
    ${getInputClasses()}
    placeholder-zinc-400
    transition-colors duration-200
  `;

          // Renderizzazione diretta per campi custom
        if (field.type === 'custom') {
    
    if (!field.customComponent) {
      return <div className="text-red-500">customComponent non definito</div>;
    }

    const CustomComponent = customFieldRegistry[field.customComponent];
    if (!CustomComponent) {
      return <div className="text-red-500">Componente custom non trovato: {field.customComponent}</div>;
    }

    // Log e normalizzazione per AddressSection
    let normalizedValue = value;
    
    // Log e normalizzazione per AddressSection
    
    if (field.customComponent === 'AddressSection') {
      try {
        // Log raw
        console.log('🏠 [FormField] PASSANDO AD ADDRESSSECTION - value:', typeof value === 'string' ? value : JSON.stringify(value, null, 2));
        // Se stringa JSON, parse
        if (typeof value === 'string') {
          const parsed = JSON.parse(value);
          if (parsed && typeof parsed === 'object') normalizedValue = parsed;
        }
      } catch {
        // ignore parse errors
      }
      // Fallback oggetto coerente
      if (!normalizedValue || typeof normalizedValue !== 'object' || Array.isArray(normalizedValue)) {
        normalizedValue = { search: '', via: '', numeroCivico: '', citta: '', cap: '', provincia: '', paese: 'Italia', lat: null, lng: null };
      }
    }

    return (
      <div className="space-y-1">
        <label className="block text-sm font-medium text-zinc-300">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <CustomComponent
          value={normalizedValue}
          onChange={onChange}
          disabled={disabled}
          required={field.required}
          placeholder={field.placeholder}
          options={field.options}
          onFocus={onFocus}
          tabIndex={tabIndex}
          readOnly={readOnly}
          // Pass additional props for specific components
          {...(field.customComponent === 'MarcaTurbinaField' && formValues ? { tipoMezzo: formValues.tipoMezzo } : {})}
        />
        {showError && error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-red-400"
          >
            {error}
          </motion.p>
        )}
      </div>
    );
  }

  // Renderizzazione per campi standard
  const renderStandardField = () => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'url':
        return (
          <input
            type={field.type === 'url' ? 'url' : field.type}
            value={value || ''}
            onChange={(e) => {
              let newValue = applyFormatter(e.target.value);

              // Apply transform based on field configuration
              if (field.transform) {
                switch (field.transform) {
                  case 'uppercase':
                    newValue = newValue.toUpperCase();
                    break;
                  case 'lowercase':
                    newValue = newValue.toLowerCase();
                    break;
                  case 'capitalize':
                    newValue = newValue.charAt(0).toUpperCase() + newValue.slice(1).toLowerCase();
                    break;
                }
              }

              // Legacy support for codice fiscale and matricola
              if (field.label?.toString().toLowerCase().includes('codice fiscale') ||
                  field.label?.toString().toLowerCase().includes('matricola')) {
                newValue = newValue.toUpperCase();
              }

              onChange(newValue);
            }}
            placeholder={field.placeholder}
            disabled={disabled}
            title={readOnlyTooltip}
            className={`${baseClasses} ${getBorderColor()} ${disabled && !readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleReadOnlyClick}
            onFocus={() => {
              setIsFocused(true);
              onFocus?.();
            }}
            onBlur={() => setIsFocused(false)}
          />
        );

      case 'phone':
        return (
          <input
            type="tel"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            title={readOnlyTooltip}
            className={`${baseClasses} ${getBorderColor()} ${disabled && !readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleReadOnlyClick}
            onFocus={() => {
              setIsFocused(true);
              onFocus?.();
            }}
            onBlur={() => setIsFocused(false)}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => {
              let newValue = e.target.value;

              // Apply transform based on field configuration
              if (field.transform) {
                switch (field.transform) {
                  case 'uppercase':
                    newValue = newValue.toUpperCase();
                    break;
                  case 'lowercase':
                    newValue = newValue.toLowerCase();
                    break;
                  case 'capitalize':
                    newValue = newValue.charAt(0).toUpperCase() + newValue.slice(1).toLowerCase();
                    break;
                }
              }

              onChange(newValue);
            }}
            placeholder={field.placeholder}
            disabled={disabled}
            title={readOnlyTooltip}
            rows={field.rows || 3}
            maxLength={field.maxLength}
            className={`${baseClasses} ${getBorderColor()} ${disabled && !readOnly ? 'opacity-50 cursor-not-allowed' : ''} resize-none`}
            onClick={handleReadOnlyClick}
            onFocus={() => {
              setIsFocused(true);
              onFocus?.();
            }}
            onBlur={() => setIsFocused(false)}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            title={readOnlyTooltip}
            className={`${baseClasses} ${getBorderColor()} ${disabled && !readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleReadOnlyClick}
            onFocus={() => {
              setIsFocused(true);
              onFocus?.();
            }}
            onBlur={() => setIsFocused(false)}
          />
        );

      case 'number':
        // Check if this is a year field to add quick year buttons
        const isYearField = field.label?.toString().toLowerCase().includes('anno');
        
        if (isYearField) {
          return (
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => {
                  const currentValue = parseInt(value || '0');
                  const newValue = Math.max(currentValue - 10, field.min || 0);
                  onChange(newValue.toString());
                }}
                disabled={disabled}
                className="px-3 py-2 text-sm bg-slate-600 hover:bg-slate-500 rounded-l border border-slate-500 border-r-0 text-slate-200 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors h-full"
              >
                -10
              </button>
              <input
                type="number"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={field.placeholder}
                disabled={disabled}
                title={readOnlyTooltip}
                min={field.min}
                max={field.max}
                className={`${baseClasses} ${getBorderColor()} ${disabled && !readOnly ? 'opacity-50 cursor-not-allowed' : ''} flex-1 rounded-none border-x-0`}
                onClick={handleReadOnlyClick}
                onFocus={() => {
                  setIsFocused(true);
                  onFocus?.();
                }}
                onBlur={() => setIsFocused(false)}
              />
              <button
                type="button"
                onClick={() => {
                  const currentValue = parseInt(value || '0');
                  const newValue = Math.min(currentValue + 10, field.max || 9999);
                  onChange(newValue.toString());
                }}
                disabled={disabled}
                className="px-3 py-2 text-sm bg-slate-600 hover:bg-slate-500 rounded-r border border-slate-500 border-l-0 text-slate-200 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors h-full"
              >
                +10
              </button>
            </div>
          );
        }

        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            title={readOnlyTooltip}
            min={field.min}
            max={field.max}
            className={`${baseClasses} ${getBorderColor()} ${disabled && !readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleReadOnlyClick}
            onFocus={() => {
              setIsFocused(true);
              onFocus?.();
            }}
            onBlur={() => setIsFocused(false)}
          />
        );

      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            title={readOnlyTooltip}
            className={`${baseClasses} ${getBorderColor()} ${disabled && !readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleReadOnlyClick}
            onFocus={() => {
              setIsFocused(true);
              onFocus?.();
            }}
            onBlur={() => setIsFocused(false)}
          >
            <option value="">{field.placeholder || 'Seleziona...'}</option>
            {field.options?.map((option: { value: string; label: string }) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <div className="flex flex-col h-full justify-center" title={readOnlyTooltip}>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={value || false}
                onChange={(e) => {
                  console.log('🔧 [FormField] Checkbox onChange:', {
                    fieldId: field.type,
                    value: e.target.checked,
                    timestamp: new Date().toISOString()
                  });
                  onChange(e.target.checked);
                }}
                onClick={(e) => {
                  console.log('🔧 [FormField] Checkbox onClick:', {
                    fieldId: field.type,
                    type: e.type,
                    target: (e.target as HTMLElement).tagName,
                    timestamp: new Date().toISOString()
                  });
                }}
                disabled={disabled}
                title={readOnlyTooltip}
                className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-zinc-600 rounded bg-zinc-700 checked:bg-blue-600 checked:border-blue-500 ${getBorderColor()}`}
                onFocus={() => {
                  setIsFocused(true);
                  onFocus?.();
                }}
                onBlur={() => setIsFocused(false)}
              />
              <label className="ml-2 text-xs text-zinc-300 flex items-center">
                {field.icon ? React.createElement(field.icon, { className: "w-4 h-4" }) : null}
                {field.label && <span className="ml-1">{field.label}</span>}
              </label>
            </div>
          </div>
        );

      case 'checkbox-multi':
        return (
          <div className="space-y-2" title={readOnlyTooltip}>
            {field.options?.map((option: { value: string; label: string }) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={Array.isArray(value) ? value.includes(option.value) : false}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([...(value || []), option.value]);
                    } else {
                      onChange((value || []).filter((v: string) => v !== option.value));
                    }
                  }}
                  disabled={disabled}
                  title={readOnlyTooltip}
                  className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-zinc-600 rounded bg-zinc-700 ${getBorderColor()}`}
                  onFocus={() => {
                    setIsFocused(true);
                    onFocus?.();
                  }}
                  onBlur={() => setIsFocused(false)}
                />
                <label className="ml-2 text-xs text-zinc-300 flex items-center">
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );

      case 'richtext':
        return (
          <div className="text-red-500">RichTextEditor temporaneamente disabilitato</div>
        );

      default:
        return <div className="text-red-500">Tipo campo non gestito: {field.type}</div>;
    }
  };

  return (
    <div className="space-y-1 relative">
      <label className="block text-sm font-medium text-zinc-300">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div onClick={handleReadOnlyClick}>
        {renderStandardField()}
      </div>
      {/* Hint per campo in readOnly mode - appare al click */}
      {showReadOnlyHint && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute right-0 -top-8 z-[9999] px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg shadow-xl whitespace-nowrap pointer-events-none border border-blue-400"
          style={{ boxShadow: '0 4px 14px rgba(37, 99, 235, 0.5)' }}
        >
          💡 Clicca "Modifica" per abilitare le modifiche
        </motion.div>
      )}
      {showError && error && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-sm text-red-400"
        >
          {error}
        </motion.p>
      )}
    </div>
  );
};

export default FormField; 